import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { map } from "rxjs/operators";
import { InspectionImageListItem, ModifiedImageUseUpdate } from "../../_objects/inspection-image";
import { CommonSnackBarService } from "../../_services/common-snack-bar.service";
import { InspectionImageService } from "../../_services/http-api/inspection-image.service";
import { MarksStateService } from "../../_services/mark-state/marks-state.service";
import { SpinnerService } from "../../_services/spinner.service";
import { INSPECTION_IMAGE_STATUS_CODE, TenantConfig, USE_IMAGE_TYPE } from "../../app.constants";
import { MarkItem } from "../../_objects/mark";
import { rotateImage } from "./image-rotation";
import { InspectionDataManageService } from "../../inspection/inspection-data-manage.service";

@Component({
  selector: "app-inspection-image-card",
  templateUrl: "./inspection-image-card.component.html",
  styleUrl: "./inspection-image-card.component.scss",
  standalone: false
})
export class InspectionImageCardComponent implements OnInit {
  readonly inspectionImageState = INSPECTION_IMAGE_STATUS_CODE;
  readonly UseImageType = USE_IMAGE_TYPE;
  readonly dragImg = document.createElement("img");
  readonly DRAG_IMAGE = "../../assets/images/ic_image_black_48dp.png";

  @Input({ required: true }) public inspectionImage: InspectionImageListItem;
  @Input() public canEditImage = true;
  @Input() public isSelected = false;
  @Input() public isSelectedForReport = false;
  @Input() public selectModeEnabled = false;
  @Input() public havePermissionOnReport = false;
  @Input() public selectForReportModeEnabled = false;
  @Input() public marks = [];

  @Output() public deleteImage = new EventEmitter<InspectionImageListItem>();
  @Output() public selectCard = new EventEmitter<{ image: InspectionImageListItem; shiftKey: boolean }>();
  @Output() public editImage = new EventEmitter<InspectionImageListItem>();
  @Output() public selectImageForReport = new EventEmitter<InspectionImageListItem>();

  public imageRotationStyle = {};
  public get videoDuration(): number | null {
    return this.inspectionImage.videoDuration ?? null;
  }

  constructor(
    private inspectionImageService: InspectionImageService,
    private oscSpinnerService: SpinnerService,
    private snackBar: CommonSnackBarService,
    private translate: TranslateService,
    private marksState: MarksStateService,
    private inspDataManageService: InspectionDataManageService
  ) {
    this.dragImg.src = this.DRAG_IMAGE;
  }

  ngOnInit(): void {
    this.imageRotationStyle = rotateImage(this.inspectionImage);
  }

  public hasError() {
    return this.inspectionImage.status == INSPECTION_IMAGE_STATUS_CODE.ERROR;
  }

  public get isLoading() {
    return this.inspectionImage.status === INSPECTION_IMAGE_STATUS_CODE.UPLOADING;
  }

  public get isCompleted() {
    return this.inspectionImage.status === INSPECTION_IMAGE_STATUS_CODE.COMPLETED;
  }

  public usedImageModeLabel() {
    switch (this.inspectionImage.usedImageMode) {
      case this.UseImageType.ORIGINAL:
        return "inspectionDataManagement.original";
      case this.UseImageType.REMEASUREMENT:
        return "inspectionDataManagement.remeasurement";
      case this.UseImageType.RETOUCHED:
        return "inspectionDataManagement.retouch";
    }
  }

  public selectImage(shiftKey: boolean = false) {
    this.selectCard.emit({ image: this.inspectionImage, shiftKey });
  }

  public selectForReport() {
    this.selectImageForReport.emit(this.inspectionImage);
    this.inspDataManageService.draftPdfIsAvailable = false;
  }

  public edit($event: Event) {
    this.editImage.emit(this.inspectionImage);
    $event.stopPropagation();
  }

  public changeDiplayedImageType(usedImageMode: USE_IMAGE_TYPE): void {
    if (this.inspectionImage.usedImageMode === usedImageMode) {
      return;
    }

    const modifiedImage: ModifiedImageUseUpdate = {
      id: this.inspectionImage.id,
      inspectionId: this.inspectionImage.inspectionId,
      usedImageMode
    };

    this.oscSpinnerService.show();
    this.inspectionImageService.putInspImageModifiedUse(modifiedImage).subscribe(
      (inspectionImageResponse) => {
        this.inspectionImage = {
          ...this.inspectionImage,
          usedImageMode,
          thumbFilePath: inspectionImageResponse.thumbFilePath
        };
        this.oscSpinnerService.hide();
      },
      (_) => {
        this.snackBar.open(this.translate.instant("inspectionDataManagement.toggleUseModifiedImageError"));
        this.oscSpinnerService.hide();
      }
    );
  }

  public getMarkColor(identifier: number) {
    const findMarkColor = (marks: MarkItem[]) => {
      const color = marks.find((mark) => mark.identifier == identifier)?.color;
      return color == undefined
        ? marks.find((m) => m.identifier == TenantConfig.DefaultMarkWhenNoCorrespondingMark.DefaultMarkIdentifier).color
        : color;
    };
    return this.marksState.markItems$.pipe(map((marks) => findMarkColor(marks)));
  }

  getFilterMark(identifier: number): MarkItem {
    return this.marks.find((mark) => mark.identifier === identifier) || new MarkItem();
  }

  public isVideo() {
    if (this.inspectionImage.mimeType) {
      return this.inspectionImage.mimeType.startsWith("video/");
    }
    return false;
  }

  public onDragStart(event: DragEvent) {
    event.dataTransfer.setDragImage(this.dragImg, 48, 48);
  }
}
